import { ArrowLeft01Icon } from '@hugeicons/core-free-icons';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';
import type { WithResponsive } from '../../utils/responsivity';
import { IconButton } from '../IconButton';
import { TextBold } from '../StyledText';

type ResponsiveMaxWidth = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

type Props = {
  title?: string;
  children: React.ReactNode;
  scrollable?: boolean;
  testID?: string;
  rightComponent?: React.ReactNode;
  onBack?: () => void;
  maxWidth?: number | ResponsiveMaxWidth;
};

export const PageContainer = ({
  title,
  children,
  scrollable = true,
  testID,
  rightComponent,
  onBack,
  maxWidth,
}: Props) => {
  const styles = useDynamicStyles(createStyles, { maxWidthProp: maxWidth });

  const content = (
    <View style={styles.contentContainer}>
      {(title || rightComponent || onBack) && (
        <View style={styles.headerContainer}>
          {onBack && (
            <IconButton
              icon={ArrowLeft01Icon}
              onPress={onBack}
              size="small"
              style={styles.backButton}
            />
          )}
          {title && (
            <TextBold style={[styles.title, onBack && styles.titleWithBack]}>
              {title}
            </TextBold>
          )}
          {rightComponent && (
            <View style={styles.rightComponent}>{rightComponent}</View>
          )}
        </View>
      )}
      <View style={styles.childrenContainer}>{children}</View>
    </View>
  );

  if (scrollable) {
    return (
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContentContainer}
        testID={testID}
      >
        {content}
      </ScrollView>
    );
  }

  return (
    <View style={styles.container} testID={testID}>
      {content}
    </View>
  );
};

const createStyles = ({
  isMobile,
  isTablet,
  isDesktop,
  maxWidthProp,
}: WithResponsive<{ maxWidthProp?: number | ResponsiveMaxWidth }>) => {
  // Determine the maxWidth based on breakpoint
  let maxWidth: number;

  if (typeof maxWidthProp === 'object' && maxWidthProp !== null) {
    // Responsive object provided
    if (isMobile && maxWidthProp.mobile !== undefined) {
      maxWidth = maxWidthProp.mobile;
    } else if (isTablet && maxWidthProp.tablet !== undefined) {
      maxWidth = maxWidthProp.tablet;
    } else if (isDesktop && maxWidthProp.desktop !== undefined) {
      maxWidth = maxWidthProp.desktop;
    } else {
      // Fallback to desktop if available, otherwise 800
      maxWidth =
        maxWidthProp.desktop ??
        maxWidthProp.tablet ??
        maxWidthProp.mobile ??
        800;
    }
  } else if (typeof maxWidthProp === 'number') {
    // Single number provided - use for all breakpoints
    maxWidth = maxWidthProp;
  } else {
    // No maxWidth provided - use default
    maxWidth = 800;
  }

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Colors.purple0,
    },
    scrollContainer: {
      flex: 1,
      backgroundColor: Colors.purple0,
    },
    scrollContentContainer: {
      flexGrow: 1,
    },
    contentContainer: {
      flex: 1,
      width: '100%',
      maxWidth,
      alignSelf: 'center',
      padding: isMobile ? 16 : isTablet ? 20 : 24,
    },
    headerContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 24,
    },
    title: {
      fontSize: 24,
      color: Colors.text,
      textAlign: 'left',
      flex: 1,
    },
    titleWithBack: {
      marginLeft: 12,
    },
    backButton: {
      marginRight: 0,
    },
    rightComponent: {
      marginLeft: 16,
    },
    childrenContainer: {
      flex: 1,
      gap: 20,
    },
  });
};
